import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../../accounting/accounting.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../../components/confirm';
import { runWithAuditReason } from '../../../api/client';
import { useFetchMs } from '../../../hooks/useFetchMs';
import {
  useCompanies, useCompany, useSaveCompany, useDeleteCompany, useTestSmtp,
  useStates, useStateCountries, useTimeZones, errMessage, type Company, type CompanyDetail,
} from './systemMasters';
import { useCities } from './city';
import { LookupSelect } from './LookupSelect';
import { DivisionDepartmentsPanel } from './DivisionDepartmentsPanel';
import { DivisionPositionsPanel } from './DivisionPositionsPanel';
import { DivisionCertificatePanel } from './DivisionCertificatePanel';

interface Props { onHome?: () => void; }

type Tab = 'basic' | 'contact' | 'logo' | 'qrcode' | 'mtf' | 'smtp';
const LANGUAGES = [{ v: 0, t: 'Chinese' }, { v: 1, t: 'English' }];
const BLANK: CompanyDetail = {
  id: '', companyName: '', webSite: '', address: '', language: 1, country: null, state: null, city: null,
  isValid: true, remark: '', contactName: '', officePhone: '', mobilePhone: '', fax: '', homePhone: '', otherPhone: '', email: '',
  zip: '', timeZone: null, logo: null, qrCode: null, measureCvFlag: null,
  enableSmtp: false, mailServer: '', portNo: null, ssl: false, fromMail: '', fromMailPassword: '', displayName: '',
};

/**
 * Settings ▸ System Management ▸ Division (the legacy Company master, shown as
 * "Division" in XAF). Phase 1 = Basic Information + Contact tabs (images, SMTP
 * config and the Departments/Positions M2M tabs are deferred). Country → State →
 * City cascade client-side; required: Division, Address, Office Phone, Country,
 * State, City. Update/Delete audit as FormName "Division"; delete is blocked when
 * employees or departments still reference it (LIMS OnDeleting parity).
 */
export function CompanyPage({ onHome }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = useCompanies();
  const loadMs = useFetchMs(isFetching);
  const save = useSaveCompany();
  const del = useDeleteCompany();

  const { data: countries = [] } = useStateCountries();
  const { data: states = [] } = useStates();
  const { data: cities = [] } = useCities();
  const { data: timezones = [] } = useTimeZones();

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [editId, setEditId] = useState<string | null | undefined>(undefined);   // undefined = closed, null = New, guid = Edit
  const { data: detail } = useCompany(editId ?? undefined);
  const testSmtp = useTestSmtp(editId ?? '');
  const [form, setForm] = useState<CompanyDetail>(BLANK);
  const [tab, setTab] = useState<Tab>('basic');
  const [btab, setBtab] = useState<'departments' | 'positions' | 'certificate'>('departments');   // bottom nested-grid tabs
  const [imgEditing, setImgEditing] = useState<Record<string, boolean>>({});   // per-image: show Browse after clicking the edit pencil
  const [err, setErr] = useState('');
  // Full-row inline edit (1st pencil) — every editable column in place, matching the legacy XAF
  // inline-edit row: Name / OfficePhone / Address / Zip as text, City / State / Country / TimeZone as
  // (cascading) dropdowns, UTC read-only (from the chosen TimeZone). The full detail is loaded so the
  // required FKs round-trip; Department (M2M) stays display-only (managed via Division Department).
  const [inlineId, setInlineId] = useState<string | null>(null);
  const [inlineForm, setInlineForm] = useState<CompanyDetail | null>(null);
  const [savingInline, setSavingInline] = useState(false);
  const { data: inlineDetail } = useCompany(inlineId ?? undefined);

  useEffect(() => { if (editId === null) setForm({ ...BLANK }); }, [editId]);
  useEffect(() => { if (detail) setForm(detail); }, [detail]);
  useEffect(() => { if (!inlineId) { setInlineForm(null); return; } if (inlineDetail) setInlineForm(inlineDetail); }, [inlineId, inlineDetail]);

  const startInline = (r: Company) => setInlineId(r.id);   // inlineForm seeds from the loaded detail
  const cancelInline = () => setInlineId(null);
  const setI = (patch: Partial<CompanyDetail>) => setInlineForm((f) => (f ? { ...f, ...patch } : f));
  const onICountry = (v: string | null) => setI({ country: v });   // no cascade — full lists
  const onIState = (v: string | null) => setI({ state: v });
  const saveInline = async () => {
    if (!inlineForm) { notify.warning('Still loading the division — try again.'); return; }
    if (!inlineForm.companyName?.trim()) { notify.warning('Division must not be empty.'); return; }
    if (!inlineForm.address?.trim()) { notify.warning('Address must not be empty.'); return; }
    if (!inlineForm.officePhone?.trim()) { notify.warning('Office Phone must not be empty.'); return; }
    if (!inlineForm.country || !inlineForm.state || !inlineForm.city) { notify.warning('Country, State and City are required.'); return; }
    // Reason-for-change gate (standing rule 11) — inline edit of an existing division.
    const reason = await gateReason('Division', 'edit');
    if (reason === null) return;
    setSavingInline(true);
    try {
      await runWithAuditReason(reason, () => save.mutateAsync(inlineForm));
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      cancelInline();
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSavingInline(false); }
  };

  const [maxed, setMaxed] = useState(false);  // open RIGHT-SIZED (not full-screen); user can maximize via the header button
  const cardRef = useRef<HTMLDivElement>(null);
  const [maxRect, setMaxRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    if (!maxed) { setMaxRect(null); return; }
    const measure = () => {
      const r = cardRef.current?.getBoundingClientRect();
      if (r) setMaxRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [maxed]);
  const maxStyle = maxed && maxRect
    ? { position: 'fixed' as const, top: maxRect.top, left: maxRect.left, width: maxRect.width,
        height: maxRect.height, maxWidth: 'none' as const, maxHeight: 'none' as const, margin: 0 }
    : undefined;

  const openNew = () => { setEditId(null); setForm({ ...BLANK }); setTab('basic'); setErr(''); setMaxed(false); };
  const openEdit = (id: string) => { setEditId(id); setTab('basic'); setErr(''); setMaxed(false); };
  const editSelected = () => {
    if (selectedIds.size !== 1) { notify.warning('Select exactly one division to edit.'); return; }
    openEdit(String([...selectedIds][0]));
  };
  const close = () => { setEditId(undefined); setMaxed(false); };

  const set = (patch: Partial<CompanyDetail>) => setForm((f) => ({ ...f, ...patch }));
  // No cascade — Country/State/City are independent full-list lookups (see opts below).
  const onCountry = (v: string | null) => set({ country: v });
  const onState = (v: string | null) => set({ state: v });

  // FULL lists, no cascade (owner decision 2026-06-25) — the legacy City→State→Country FKs are
  // inconsistent in this data, so strict cascading would hide valid options. Matches the Employee form.
  const countryOpts = useMemo(() => countries.map((c) => ({ id: c.id, label: c.name })), [countries]);
  const stateOpts = useMemo(() => states.map((s) => ({ id: s.id, label: s.longName ?? s.shortName ?? '' })), [states]);
  const cityOpts = useMemo(() => cities.map((c) => ({ id: c.id, label: c.cityName ?? '' })), [cities]);
  const dlgUtc = useMemo(() => timezones.find((t) => t.id === form.timeZone)?.utc ?? '', [timezones, form.timeZone]);

  const doSave = async (after: 'stay' | 'close' | 'new') => {
    if (!form.companyName?.trim()) { setTab('basic'); setErr('Division must not be empty.'); return; }
    if (!form.address?.trim()) { setTab('basic'); setErr('Address must not be empty.'); return; }
    if (!form.country) { setTab('basic'); setErr('Country must not be empty.'); return; }
    if (!form.state) { setTab('basic'); setErr('State must not be empty.'); return; }
    if (!form.city) { setTab('basic'); setErr('City must not be empty.'); return; }
    if (!form.officePhone?.trim()) { setTab('basic'); setErr('Office Phone must not be empty.'); return; }
    if (!form.timeZone) { setTab('basic'); setErr('Time Zone must not be empty.'); return; }
    setErr('');
    const wasNew = !form.id;
    // Reason-for-change gate (standing rule 11) — only on an EDIT; Create is exempt.
    let reason = '';
    if (!wasNew) {
      const r = await gateReason('Division', 'edit');
      if (r === null) return;
      reason = r;
    }
    let newId: string | null = form.id || null;
    await runSave({
      save: () => runWithAuditReason(reason, async () => { newId = await save.mutateAsync(form); }),
      successMessage: 'Saved successfully.',
      onSaved: () => {
        if (after === 'close') { close(); return; }
        if (after === 'new') { openNew(); return; }
        if (wasNew && newId) setEditId(newId);   // new → switch to edit mode on the fresh row
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one division to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} division${selectedIds.size === 1 ? '' : 's'}?` })) return;
    const reason = await gateReason('Division', 'delete');
    if (reason === null) return;
    try { await runWithAuditReason(reason, async () => { for (const id of selectedIds) await del.mutateAsync(String(id)); }); setSelectedIds(new Set()); await alert({ title: 'Success', message: 'Deleted.', tone: 'success' }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => [r.name, r.officePhone, r.address, r.city, r.state, r.country].some((v) => (v ?? '').toLowerCase().includes(q))) : rows;
  }, [rows, search]);

  const cmp = (get: (r: Company) => string | null) => (a: Company, b: Company) =>
    (get(a) ?? '').localeCompare(get(b) ?? '', undefined, { sensitivity: 'base' });

  // Inline (in-grid) cascading option lists for the edit row.
  // Inline edit reuses the same FULL (non-cascading) State/City lists as the dialog.
  const iStateOpts = useMemo(() => states.map((s) => ({ id: s.id, label: s.longName ?? s.shortName ?? '' })), [states]);
  const iCityOpts = useMemo(() => cities.map((c) => ({ id: c.id, label: c.cityName ?? '' })), [cities]);
  const tzOpts = useMemo(() => timezones.filter((t) => t.active).map((t) => ({ id: t.id, label: t.timeZone ?? '' })), [timezones]);
  const iUtc = useMemo(() => timezones.find((t) => t.id === inlineForm?.timeZone)?.utc ?? '', [timezones, inlineForm?.timeZone]);

  const isEd = (r: Company) => inlineId === r.id && inlineForm != null;
  const iText = (r: Company, value: string, cur: string, set: (s: string) => void) =>
    isEd(r) ? (
      <input className="pts-input" value={cur} disabled={savingInline}
        onClick={(e) => e.stopPropagation()} onChange={(e) => set(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(); if (e.key === 'Escape') cancelInline(); }} />
    ) : (<span>{value}</span>);
  const iSelect = (r: Company, value: string, cur: string | null, opts: { id: string; label: string }[], onChange: (v: string | null) => void) =>
    isEd(r) ? (
      <select className="pts-input" value={cur ?? ''} disabled={savingInline} onClick={(e) => e.stopPropagation()} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">N/A</option>
        {opts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    ) : (<span>{value}</span>);

  // Columns mirror the legacy Company_ListView (Name, OfficePhone, Address, City, State, Zip,
  // Country, Department, TimeZone, UTC). Inline edit makes the whole row editable like the XAF
  // inline-edit row — text for Name/OfficePhone/Address/Zip, dropdowns for City/State/Country/
  // TimeZone (cascading), UTC read-only (from the chosen TimeZone), Department display-only.
  const columns: AppColumn<Company>[] = [
    {
      field: 'inlineedit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) =>
        inlineId === r.id ? (
          <span className="pts-rowedit" onClick={(e) => e.stopPropagation()}>
            <button className="pts-icon-btn save" title="Save" disabled={savingInline} onClick={() => void saveInline()}><i className="ti ti-device-floppy" /></button>
            <button className="pts-icon-btn cancel" title="Cancel" disabled={savingInline} onClick={cancelInline}><i className="ti ti-arrow-back-up" /></button>
          </span>
        ) : (
          <button className="pts-icon-btn edit" title="Inline edit" onClick={(e) => { e.stopPropagation(); startInline(r); }}><i className="ti ti-edit" /></button>
        ),
    },
    {
      field: 'editopen', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) => inlineId === r.id ? null : (
        <button className="pts-icon-btn edit" title="Open full edit screen" onClick={(e) => { e.stopPropagation(); openEdit(r.id); }}><i className="ti ti-external-link" /></button>
      ),
    },
    {
      // LIMS Name column is narrow — long names WRAP to two lines (matched here via width + normal wrap).
      field: 'name', header: 'Name', width: 190, text: (r) => r.name ?? '', compare: cmp((r) => r.name),
      render: (r) => isEd(r)
        ? (<input className="pts-input" autoFocus value={inlineForm?.companyName ?? ''} disabled={savingInline}
            onClick={(e) => e.stopPropagation()} onChange={(e) => setI({ companyName: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(); if (e.key === 'Escape') cancelInline(); }} />)
        : (<span style={{ whiteSpace: 'normal', display: 'block' }}>{r.name ?? ''}</span>),
    },
    { field: 'officePhone', header: 'OfficePhone', minWidth: 140, sortable: false, text: (r) => r.officePhone ?? '', render: (r) => iText(r, r.officePhone ?? '', inlineForm?.officePhone ?? '', (v) => setI({ officePhone: v })) },
    { field: 'address', header: 'Address', minWidth: 170, sortable: false, text: (r) => r.address ?? '', render: (r) => iText(r, r.address ?? '', inlineForm?.address ?? '', (v) => setI({ address: v })) },
    { field: 'city', header: 'City', minWidth: 120, sortable: false, text: (r) => r.city ?? '', render: (r) => iSelect(r, r.city ?? '', inlineForm?.city ?? null, iCityOpts, (v) => setI({ city: v })) },
    { field: 'state', header: 'State', minWidth: 110, sortable: false, text: (r) => r.state ?? '', render: (r) => iSelect(r, r.state ?? '', inlineForm?.state ?? null, iStateOpts, onIState) },
    { field: 'zip', header: 'Zip', minWidth: 80, sortable: false, text: (r) => r.zip ?? '', render: (r) => iText(r, r.zip ?? '', inlineForm?.zip ?? '', (v) => setI({ zip: v })) },
    { field: 'country', header: 'Country', minWidth: 140, sortable: false, text: (r) => r.country ?? '', render: (r) => iSelect(r, r.country ?? '', inlineForm?.country ?? null, countryOpts, onICountry) },
    { field: 'department', header: 'Department', minWidth: 130, sortable: false, text: (r) => r.department ?? '' },
    { field: 'timeZone', header: 'TimeZone', minWidth: 150, sortable: false, text: (r) => r.timeZone ?? '', render: (r) => iSelect(r, r.timeZone ?? '', inlineForm?.timeZone ?? null, tzOpts, (v) => setI({ timeZone: v })) },
    {
      field: 'utc', header: 'UTC', minWidth: 90, sortable: false, text: (r) => r.utc ?? '',
      render: (r) => isEd(r) ? (<input className="pts-input" value={iUtc} disabled readOnly />) : (<span>{r.utc ?? ''}</span>),
    },
  ];

  // ── Dialog field helpers ────────────────────────────────────────────────────
  const text = (label: string, key: keyof CompanyDetail, required = false) => (
    <div className="acc-field-h">
      <label className={`acc-label${required ? ' acc-label-req' : ''}`}>{label}{required && <span className="req">*</span>}</label>
      <div className="acc-fh-control">
        <input className="acc-input" value={(form[key] as string) ?? ''} onChange={(e) => { set({ [key]: e.target.value } as Partial<CompanyDetail>); if (err) setErr(''); }} />
      </div>
    </div>
  );
  const lookup = (label: string, key: 'country' | 'state' | 'city', opts: { id: string; label: string }[], onChange: (v: string | null) => void, required = false) => (
    <div className="acc-field-h">
      <label className={`acc-label${required ? ' acc-label-req' : ''}`}>{label}{required && <span className="req">*</span>}</label>
      <div className="acc-fh-control acc-lookup">
        <LookupSelect value={(form[key] as string) ?? null} options={opts} onChange={(v) => { onChange(v); if (err) setErr(''); }} />
        {form[key] && <button className="acc-lookup-btn" title="Erase" onClick={() => onChange(null)}><i className="ti ti-eraser" /></button>}
      </div>
    </div>
  );

  // Image tab (Logo / QR Code / Mandatory Testing Flag) — LIMS shows the image with edit/erase icons;
  // the Browse button appears only AFTER clicking the edit pencil (empty → Browse straight away).
  // Stored as varbinary (byte[]); base64 round-trips via JSON, like the Employee Photo/Signature.
  const onPickImage = (key: 'logo' | 'qrCode' | 'measureCvFlag', file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { const r = String(reader.result); set({ [key]: r.includes(',') ? r.split(',')[1] : r } as Partial<CompanyDetail>); };
    reader.readAsDataURL(file);
    setImgEditing((s) => ({ ...s, [key]: false }));
  };
  const imageField = (label: string, key: 'logo' | 'qrCode' | 'measureCvFlag') => {
    const b64 = form[key];
    const editing = imgEditing[key] ?? false;
    const browse = (text = 'Browse…') => (
      <label className="ca-btn acc-img-browse">{text}
        <input type="file" accept="image/*" hidden onChange={(e) => onPickImage(key, e.target.files?.[0])} />
      </label>
    );
    return (
      <div className="acc-tab-pane">
        <div className="acc-img-field">
          <label className="acc-label">{label}</label>
          {b64 ? (
            <>
              <div className="acc-img-box"><img src={`data:image/png;base64,${b64}`} alt={label} /></div>
              <div className="acc-img-acts">
                <button type="button" className="pts-icon-btn edit" title="Edit" onClick={() => setImgEditing((s) => ({ ...s, [key]: true }))}><i className="ti ti-edit" /></button>
                <button type="button" className="pts-icon-btn" title="Erase" onClick={() => { set({ [key]: null } as Partial<CompanyDetail>); setImgEditing((s) => ({ ...s, [key]: false })); }}><i className="ti ti-eraser" /></button>
              </div>
              {editing && <div className="acc-img-acts">{browse()}</div>}
            </>
          ) : (
            <>
              <div className="acc-img-box acc-img-box-empty"><i className="ti ti-photo acc-img-ph" /></div>
              <div className="acc-img-acts">{browse()}</div>
            </>
          )}
        </div>
      </div>
    );
  };

  const doTestSmtp = async () => {
    if (!editId) { notify.warning('Save the division first, then click Test Connection.'); return; }
    if (!form.mailServer?.trim()) { notify.warning('Mail Server must not be empty.'); return; }
    try {
      // Tests the SAVED SMTP config (server decrypts the password) — Save any changes first.
      // Show the result CENTERED on the form (shared confirm dialog), not the top toast.
      const r = await testSmtp.mutateAsync();
      await alert({ title: r.ok ? 'Success' : 'SMTP Test', message: r.message, tone: r.ok ? 'success' : 'error' });
    } catch (e) { notify.error(errMessage(e)); }
  };

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Division</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        <FormTitle pageId="sm-division" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Division" />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>
      <div className="acc-body">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        <AppToolbar
          onNew={openNew}
          onEdit={editSelected} editDisabled={selectedIds.size !== 1}
          onDelete={() => void removeSelected()} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="Division"
                headers={['Name', 'OfficePhone', 'Address', 'City', 'State', 'Zip', 'Country', 'Department', 'TimeZone', 'UTC']}
                rows={visible.map((r) => [r.name ?? '', r.officePhone ?? '', r.address ?? '', r.city ?? '', r.state ?? '', r.zip ?? '', r.country ?? '', r.department ?? '', r.timeZone ?? '', r.utc ?? ''])} />
            </span>
          }
        />
        {/* Search lives in its OWN row BELOW the toolbar (standard) — never inside the card. */}
        <div className="list-search-row">
          <div className="list-search"><span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. */}
        <div className="acc-card" ref={cardRef}>
          <AppDataGrid<Company> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds} selectedRowId={inlineId ?? editId ?? null}
            initialSort={{ key: 'name', dir: 'asc' }} loading={isFetching} emptyText="No divisions found." />
        </div>
      </div>

      {editId !== undefined && (
        <div className="acc-overlay" onMouseDown={close}>
          <div className={`acc-dialog acc-dialog-lg acc-dialog-emp${maxed ? ' acc-dialog-max' : ''}`} style={maxStyle} onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr">
              <span>{editId ? `Division - ${form.companyName ?? ''}` : 'Division'}</span>
              <span className="acc-dialog-acts">
                <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
                <i className="ti ti-x acc-dialog-x" onClick={close} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-tabs">
                <button type="button" className={`acc-tab${tab === 'basic' ? ' active' : ''}`} onClick={() => setTab('basic')}><i className="ti ti-info-circle" /> Basic Information</button>
                <button type="button" className={`acc-tab${tab === 'contact' ? ' active' : ''}`} onClick={() => setTab('contact')}><i className="ti ti-address-book" /> Contact</button>
                <button type="button" className={`acc-tab${tab === 'logo' ? ' active' : ''}`} onClick={() => setTab('logo')}><i className="ti ti-photo" /> Logo</button>
                <button type="button" className={`acc-tab${tab === 'qrcode' ? ' active' : ''}`} onClick={() => setTab('qrcode')}><i className="ti ti-qrcode" /> QR Code</button>
                <button type="button" className={`acc-tab${tab === 'mtf' ? ' active' : ''}`} onClick={() => setTab('mtf')}><i className="ti ti-checkbox" /> Mandatory Testing Flag</button>
                <button type="button" className={`acc-tab${tab === 'smtp' ? ' active' : ''}`} onClick={() => setTab('smtp')}><i className="ti ti-mail-cog" /> SMTP Configuration</button>
              </div>

              {tab === 'basic' && (
                <div className="acc-tab-pane">
                  {/* 3-column layout matching the legacy Company_DetailView Basic Information group. */}
                  <div className="acc-emp-2col acc-div-basic">
                    <div className="acc-emp-col">
                      {text('Division', 'companyName', true)}
                      {text('Web Site', 'webSite')}
                      {text('Office Phone', 'officePhone', true)}
                      {text('Address', 'address', true)}
                      <div className="acc-field-h">
                        <label className="acc-label">Language</label>
                        <div className="acc-fh-control" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                          {LANGUAGES.map((l) => (
                            <label key={l.v} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                              <input type="radio" name="div-lang" checked={(form.language ?? 1) === l.v} onChange={() => set({ language: l.v })} /> {l.t}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="acc-emp-col">
                      {lookup('City', 'city', cityOpts, (v) => set({ city: v }), true)}
                      {lookup('State', 'state', stateOpts, onState, true)}
                      {text('Zip Code', 'zip')}
                      {lookup('Country', 'country', countryOpts, onCountry, true)}
                      {/* LIMS renders this boolean as [checkbox] Is Valid (checkbox first), at the control column. */}
                      <div className="acc-field-h">
                        <label className="acc-label" />
                        <div className="acc-fh-control">
                          <label className="acc-check-inline"><input type="checkbox" checked={form.isValid} onChange={(e) => set({ isValid: e.target.checked })} /> Is Valid</label>
                        </div>
                      </div>
                    </div>
                    <div className="acc-emp-col">
                      <div className="acc-field-h">
                        <label className="acc-label acc-label-req">Time Zone<span className="req">*</span></label>
                        <div className="acc-fh-control acc-lookup">
                          <LookupSelect value={form.timeZone ?? null} options={tzOpts} onChange={(v) => set({ timeZone: v })} />
                          {form.timeZone && <button className="acc-lookup-btn" title="Erase" onClick={() => set({ timeZone: null })}><i className="ti ti-eraser" /></button>}
                        </div>
                      </div>
                      <div className="acc-field-h">
                        <label className="acc-label acc-label-req">UTC<span className="req">*</span></label>
                        <div className="acc-fh-control"><input className="acc-input" value={dlgUtc} disabled readOnly /></div>
                      </div>
                    </div>
                  </div>
                  <div className="acc-field-h acc-div-remark">
                    <label className="acc-label">Remark</label>
                    <div className="acc-fh-control"><textarea className="acc-input acc-text-multi" rows={2} value={form.remark ?? ''} onChange={(e) => set({ remark: e.target.value })} /></div>
                  </div>
                </div>
              )}

              {tab === 'contact' && (
                // LIMS Contact layout: Contact Name full-width on top, then two WIDE columns.
                <div className="acc-tab-pane acc-contact">
                  {text('Contact Name', 'contactName')}
                  <div className="acc-contact-cols">
                    <div className="acc-emp-col">
                      {text('Office Phone', 'officePhone', true)}
                      {text('Mobile Phone', 'mobilePhone')}
                      {text('Fax', 'fax')}
                    </div>
                    <div className="acc-emp-col">
                      {text('Home Phone', 'homePhone')}
                      {text('Other Phone', 'otherPhone')}
                      {text('Email', 'email')}
                    </div>
                  </div>
                </div>
              )}

              {tab === 'logo' && imageField('Logo', 'logo')}
              {tab === 'qrcode' && imageField('QR Code', 'qrCode')}
              {tab === 'mtf' && imageField('Measure CV Flag', 'measureCvFlag')}

              {tab === 'smtp' && (
                // SMTP Configuration (LIMS): fields disable when Enable SMTP is off (Appearance "Disable SMTP").
                <div className="acc-tab-pane acc-compact">
                  <div className="acc-field-h">
                    <label className="acc-label" />
                    <div className="acc-fh-control"><label className="acc-check-inline"><input type="checkbox" checked={form.enableSmtp} onChange={(e) => set({ enableSmtp: e.target.checked })} /> Enable SMTP</label></div>
                  </div>
                  <div className="acc-field-h">
                    <label className="acc-label">Mail Server</label>
                    <div className="acc-fh-control"><input className="acc-input" value={form.mailServer ?? ''} disabled={!form.enableSmtp} onChange={(e) => set({ mailServer: e.target.value })} /></div>
                  </div>
                  <div className="acc-field-h">
                    <label className="acc-label">Port</label>
                    <div className="acc-fh-control"><input className="acc-input" type="number" value={form.portNo ?? ''} disabled={!form.enableSmtp} onChange={(e) => set({ portNo: e.target.value === '' ? null : Number(e.target.value) })} /></div>
                  </div>
                  <div className="acc-field-h">
                    <label className="acc-label" />
                    <div className="acc-fh-control"><label className="acc-check-inline"><input type="checkbox" checked={form.ssl} disabled={!form.enableSmtp} onChange={(e) => set({ ssl: e.target.checked })} /> SSL</label></div>
                  </div>
                  <div className="acc-field-h">
                    <label className="acc-label">From Mail</label>
                    <div className="acc-fh-control"><input className="acc-input" value={form.fromMail ?? ''} disabled={!form.enableSmtp} onChange={(e) => set({ fromMail: e.target.value })} /></div>
                  </div>
                  <div className="acc-field-h">
                    <label className="acc-label">From Mail Password</label>
                    <div className="acc-fh-control"><input className="acc-input" type="password" autoComplete="new-password" value={form.fromMailPassword ?? ''} disabled={!form.enableSmtp} onChange={(e) => set({ fromMailPassword: e.target.value })} /></div>
                  </div>
                  <div className="acc-field-h">
                    <label className="acc-label">Display Name</label>
                    <div className="acc-fh-control"><input className="acc-input" value={form.displayName ?? ''} disabled={!form.enableSmtp} onChange={(e) => set({ displayName: e.target.value })} /></div>
                  </div>
                  <div className="acc-field-h">
                    <label className="acc-label" />
                    <div className="acc-fh-control">
                      <button type="button" className="ca-btn" disabled={!form.enableSmtp || testSmtp.isPending} onClick={() => void doTestSmtp()}>{testSmtp.isPending ? 'Testing…' : 'Test Connection'}</button>
                    </div>
                  </div>
                </div>
              )}

              {err && <div className="acc-err" style={{ marginTop: 8 }}>{err}</div>}

              {/* Bottom nested-grid tabs (Departments / Positions / Certificate), like the LIMS detail. */}
              <div className="acc-tabs" style={{ marginTop: 16 }}>
                <button type="button" className={`acc-tab${btab === 'departments' ? ' active' : ''}`} onClick={() => setBtab('departments')}><i className="ti ti-sitemap" /> Departments</button>
                <button type="button" className={`acc-tab${btab === 'positions' ? ' active' : ''}`} onClick={() => setBtab('positions')}><i className="ti ti-id-badge-2" /> Positions</button>
                <button type="button" className={`acc-tab${btab === 'certificate' ? ' active' : ''}`} onClick={() => setBtab('certificate')}><i className="ti ti-certificate" /> Certificate</button>
              </div>
              {btab === 'departments' && <DivisionDepartmentsPanel companyId={editId ?? undefined} />}
              {btab === 'positions' && <DivisionPositionsPanel companyId={editId ?? undefined} />}
              {btab === 'certificate' && <DivisionCertificatePanel companyId={editId ?? undefined} />}
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void doSave('stay')} disabled={save.isPending}><span className="tb-i">💾</span> Save</button>
              <button className="ca-btn" onClick={() => void doSave('close')} disabled={save.isPending}><span className="tb-i">💾</span> Save and Close</button>
              <button className="ca-btn" onClick={() => void doSave('new')} disabled={save.isPending}><span className="tb-i">💾</span> Save and New</button>
              <button className="ca-btn" onClick={close}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
